
import React, { useState, useRef, useEffect } from 'react';
import { Job, Asset, Customer, Site, ConnectedItem, ServiceRecord, AssetCategory, Contact, SiteContact, Part, RentalAsset, RentalContract, RentalInspection, RentalStatus, Technician, JobStatus, JobLog, JobChecklistItem, JobPart, QuoteRequest } from '../../types';
import { 
  Calendar, MapPin, Wrench, AlertTriangle, CheckCircle, Truck, Battery, 
  ChevronRight, ArrowLeft, Factory, Building, FileText, Activity, MoreVertical,
  Wind, Zap, Gauge, QrCode, X, Camera, Plus, Save, User, Phone, Mail, Trash2,
  CreditCard, Briefcase, Shield, Megaphone, Users, Lock, HardHat, DollarSign,
  Box, FileCheck, Link as LinkIcon, Search, Filter, ClipboardCheck, ArrowRightLeft,
  Timer, List, Layout, Smartphone, CheckSquare, Clock, Navigation, PenTool,
  ThumbsUp, ThumbsDown, LogOut, Ticket, Flame, AlertOctagon, FileSignature, Ruler
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import jsQR from 'jsqr';

// --- HELPER COMPONENTS ---

const SpecRow = ({ label, value, icon }: { label: string, value: any, icon?: React.ReactNode }) => (
    <div className="flex justify-between items-center py-1">
       <span className="text-slate-400 flex items-center gap-2 text-sm">{icon} {label}</span>
       <span className="text-white font-mono text-sm">{value || '-'}</span>
    </div>
);

const Modal = ({ title, onClose, children, size='lg' }: { title: string, onClose: (e?: any) => void, children: React.ReactNode, size?: 'md'|'lg'|'xl' }) => (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-fade-in">
        <div className={`bg-slate-800 rounded-lg border border-slate-700 w-full shadow-2xl flex flex-col max-h-[90vh] ${size === 'xl' ? 'max-w-6xl' : size === 'lg' ? 'max-w-4xl' : 'max-w-2xl'}`}>
            <div className="p-4 border-b border-slate-700 flex justify-between items-center">
                <h3 className="text-xl font-bold text-white">{title}</h3>
                <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5"/></button>
            </div>
            <div className="flex flex-1 overflow-hidden overflow-y-auto">
                {children}
            </div>
        </div>
    </div>
);

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
    label: string;
}

const Input: React.FC<InputProps> = ({ label, className = "", ...props }) => (
    <div>
        <label className="block text-xs text-slate-400 uppercase font-bold mb-1">{label}</label>
        <input 
            className={`w-full bg-slate-900 border border-slate-700 text-white p-2 rounded focus:border-titan-500 outline-none focus:ring-1 focus:ring-titan-500 [&::-webkit-calendar-picker-indicator]:invert ${className}`} 
            {...props}
        />
    </div>
);

const Select = ({ label, name, options, ...props }: any) => (
    <div>
        <label className="block text-xs text-slate-400 uppercase font-bold mb-1">{label}</label>
        <select name={name} className="w-full bg-slate-900 border border-slate-700 text-white p-2 rounded focus:border-titan-500 outline-none" {...props}>
            {options.map((opt: string) => <option key={opt} value={opt}>{opt}</option>)}
        </select>
    </div>
);

const SubmitButton = ({ label }: { label: string }) => (
    <button type="submit" className="w-full bg-titan-500 hover:bg-titan-600 text-white font-bold py-3 rounded mt-4 transition shadow-lg shadow-titan-500/20">
        {label}
    </button>
);

const StatusBadge = ({ status }: { status: string }) => {
    const colors: any = {
        'New': 'bg-blue-500/20 text-blue-400',
        'Planned': 'bg-purple-500/20 text-purple-400',
        'Allocated': 'bg-orange-500/20 text-orange-400',
        'In Progress': 'bg-yellow-500/20 text-yellow-400',
        'Completed': 'bg-green-500/20 text-green-400',
        'Closed': 'bg-slate-700 text-slate-300',
        'Active': 'bg-green-500/20 text-green-400',
        'Available': 'bg-green-500/20 text-green-400',
        'On Rent': 'bg-blue-500/20 text-blue-400',
        'Reserved': 'bg-yellow-500/20 text-yellow-400',
        'Maintenance': 'bg-red-500/20 text-red-400',
        'Pending': 'bg-orange-500/20 text-orange-400'
    };
    return <span className={`px-2 py-1 rounded text-xs font-bold uppercase ${colors[status] || 'bg-slate-700 text-slate-300'}`}>{status}</span>;
};

// --- STANDARD 20 POINT CHECKLIST ---
const standard20PointCheck: JobChecklistItem[] = [
    { id: 'CHK-01', label: 'Oil Level Correct', status: 'Pending' },
    { id: 'CHK-02', label: 'Coolant Level / Condition', status: 'Pending' },
    { id: 'CHK-03', label: 'Air Leaks Check', status: 'Pending' },
    { id: 'CHK-04', label: 'Oil Leaks Check', status: 'Pending' },
    { id: 'CHK-05', label: 'Panel Warnings / Alarms', status: 'Pending' },
    { id: 'CHK-06', label: 'Emergency Stop Function', status: 'Pending' },
    { id: 'CHK-07', label: 'Safety Guards Secure', status: 'Pending' },
    { id: 'CHK-08', label: 'Drive Belt Tension/Condition', status: 'Pending' },
    { id: 'CHK-09', label: 'Coupling Element Condition', status: 'Pending' },
    { id: 'CHK-10', label: 'Motor Temperature', status: 'Pending' },
    { id: 'CHK-11', label: 'Airend Discharge Temp', status: 'Pending' },
    { id: 'CHK-12', label: 'Vibration Level Normal', status: 'Pending' },
    { id: 'CHK-13', label: 'Electrical Connections Tight', status: 'Pending' },
    { id: 'CHK-14', label: 'Voltage & Amps Check', status: 'Pending' },
    { id: 'CHK-15', label: 'Condensate Drains Working', status: 'Pending' },
    { id: 'CHK-16', label: 'Intake Filter Condition', status: 'Pending' },
    { id: 'CHK-17', label: 'Oil Filter Condition', status: 'Pending' },
    { id: 'CHK-18', label: 'Separator DP Check', status: 'Pending' },
    { id: 'CHK-19', label: 'Cooler Cleanliness', status: 'Pending' },
    { id: 'CHK-20', label: 'General Machine Cleanliness', status: 'Pending' },
];

// --- MOCK DATA INITIALIZATION ---
const initialTechnicians: Technician[] = [
    { id: 'TECH-001', name: 'J. Smith', skills: ['Screw Compressor', 'Electrical', 'Refrigeration'], status: 'Available' },
    { id: 'TECH-002', name: 'P. Nkosi', skills: ['Diesel Portable', 'Mechanical', 'Hydraulics'], status: 'Busy', currentJobId: 'JOB-2024-002' },
    { id: 'TECH-003', name: 'S. Naidoo', skills: ['Assistant', 'Basic Service'], status: 'Available' }
];

const initialJobs: Job[] = [
  { 
      id: 'JOB-2024-001', origin: 'Service CRM', customer: 'Anglo Mining', site: 'Rustenburg Shaft 2', machineName: 'GA75', type: 'Breakdown', status: 'Allocated', priority: 'High', technician: 'J. Smith', dateCreated: '2024-05-12', description: 'Compressor tripped on high temp. Urgent attention required.', machineId: 'CP-001', 
      logs: [], parts: [], checklist: [], checklist20: standard20PointCheck, quoteRequests: [],
      dueDate: '2024-05-13', permitRequired: true, jhaCompleted: false
  },
  { 
      id: 'JOB-2024-002', origin: 'Service CRM', customer: 'Toyota Mfg', site: 'Durban Plant', machineName: 'R160', type: 'Planned Service', status: 'Allocated', priority: 'Medium', technician: 'J. Smith', dateCreated: '2024-05-14', description: '2000hr Service as per SLA.', machineId: 'CP-002',
      logs: [], parts: [], checklist: [], checklist20: standard20PointCheck, quoteRequests: [],
      dueDate: '2024-05-20'
  }
];

const initialAssets: Asset[] = [
  { 
      id: 'CP-001', siteId: 'SITE-001', customerId: 'CUST-001', tag: 'CMP-552', name: 'Atlas Copco GA75', category: 'Screw Compressor', status: 'Active', nextInspection: '2024-06-01', location: 'Rustenburg Shaft 2', make: 'Atlas Copco', model: 'GA75', serialNumber: 'API-8821992', kw: 75, pressure: 8.5, flow: 480, voltage: '400V 3Ph', hoursReading: 24500, installDate: '2019-03-15', warrantyEnd: '2021-03-15', criticality: 'High', airendSerial: 'AIR-9922', vsd: true, coolingType: 'Air', 
      billOfMaterials: [
          { id: 'P-001', partNumber: '1613-6105-00', description: 'Oil Separator Element', quantity: 1, isCritical: true },
          { id: 'P-002', partNumber: '1613-9001-00', description: 'Air Filter Element', quantity: 1, isCritical: false },
          { id: 'P-003', partNumber: '2901-0566-00', description: 'Oil Filter', quantity: 1, isCritical: true },
          { id: 'P-004', partNumber: '2901-1463-00', description: 'Service Kit 4000h', quantity: 1, isCritical: false },
      ] 
  },
  { 
      id: 'CP-002', siteId: 'SITE-002', customerId: 'CUST-002', tag: 'CMP-102', name: 'Ingersoll Rand R160', category: 'Screw Compressor', status: 'Under Repair', nextInspection: '2024-05-10', location: 'Durban Plant', make: 'Ingersoll Rand', model: 'R160', serialNumber: 'IR-22100', kw: 160, pressure: 10, flow: 850, voltage: '400V 3Ph', hoursReading: 12000, installDate: '2021-01-20', warrantyEnd: '2024-01-20', criticality: 'Medium', vsd: false, coolingType: 'Water',
      billOfMaterials: [
          { id: 'P-005', partNumber: '39903281', description: 'Coolant Filter', quantity: 1, isCritical: true },
          { id: 'P-006', partNumber: '39903265', description: 'Air Filter', quantity: 1, isCritical: true },
      ]
  },
];

const initialCustomers: Customer[] = [
  { id: 'CUST-001', name: 'Anglo American', group: 'Anglo Platinum', vat: '440012399', billingAddress: '55 Main Rd, JHB', industry: 'Mining', status: 'Active', activeSites: 4, type: 'Active', contacts: [], mainContact: { name: 'P. Jacobs', email: 'p.jacobs@anglo.com', phone: '+27 82 555 1234'} },
  { id: 'CUST-002', name: 'Toyota Manufacturing', group: 'Toyota Global', vat: '49921022', billingAddress: 'Prospecton Rd, Durban', industry: 'Automotive', status: 'Active', activeSites: 1, type: 'Active', contacts: [], mainContact: { name: 'S. Naidoo', email: 's.naidoo@toyota.co.za', phone: '+27 83 444 9988'} },
];

const initialSites: Site[] = [
  { id: 'SITE-001', customerId: 'CUST-001', name: 'Rustenburg Shaft 2', address: 'R56 Main Rd, Rustenburg', city: 'Rustenburg', contact: { name: 'J. Foreman', email: 'j.foreman@anglo.com', phone: '011 222 3333'}, hsseRequirements: 'Red Ticket required, Hard Hat, Reflective Vest', status: 'Active', contacts: [], billTo: 'Head Office', poRequired: true },
  { id: 'SITE-002', customerId: 'CUST-002', name: 'Durban Plant', address: '1 Toyota Rd, Prospecton', city: 'Durban', contact: { name: 'K. Chetty', email: 'k.chetty@toyota.com', phone: '031 999 8888'}, hsseRequirements: 'Safety Shoes, Hearing Protection', status: 'Active', contacts: [], billTo: 'Head Office', poRequired: true },
];

const initialRentalFleet: RentalAsset[] = [
    {
        id: 'RA-001', assetId: 'CP-001', fleetNumber: 'DF-400', make: 'Doosan', model: '7/41', type: 'Diesel Portable', specs: '140 CFM, 7 Bar',
        status: 'Available', depot: 'Durban', dailyRate: 850, weeklyRate: 4000, monthlyRate: 12000, bookValue: 150000, purchaseDate: '2022-01-15', utilizationYTD: 65
    },
    {
        id: 'RA-002', assetId: 'CP-005', fleetNumber: 'EF-100', make: 'Atlas Copco', model: 'GA30', type: 'Electric Screw', specs: '175 CFM, 8 Bar',
        status: 'On Rent', depot: 'Johannesburg', dailyRate: 1200, weeklyRate: 5500, monthlyRate: 16500, bookValue: 220000, purchaseDate: '2023-03-10', utilizationYTD: 80,
        currentLocation: 'Defy Appliances', linkedContractId: 'CTR-2024-001'
    }
];

const initialContracts: RentalContract[] = [
    {
        id: 'CTR-2024-001', customer: 'Defy Appliances', site: 'Jacobs Factory', startDate: '2024-04-01', endDate: '2024-06-30', status: 'Active', billingCycle: 'Monthly',
        assets: [initialRentalFleet[1]]
    }
];

const CreateJobModal = ({ onClose, onSave }: { onClose: () => void, onSave: (job: Job) => void }) => {
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const formData = new FormData(e.target as HTMLFormElement);
        const newJob: Job = {
            id: `JOB-${Date.now()}`,
            origin: 'Manual',
            customer: formData.get('customer') as string,
            site: formData.get('site') as string,
            machineName: formData.get('machine') as string,
            type: formData.get('type') as any,
            priority: formData.get('priority') as any,
            status: 'New',
            dateCreated: new Date().toISOString().split('T')[0],
            description: formData.get('description') as string,
            technician: 'Unassigned',
            logs: [], parts: [], checklist: [], checklist20: standard20PointCheck, quoteRequests: []
        };
        onSave(newJob);
    }
    return (
        <Modal title="Create New Job" onClose={onClose}>
            <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                    <Input label="Customer" name="customer" required />
                    <Input label="Site" name="site" required />
                </div>
                <Input label="Machine / Asset" name="machine" />
                <div className="grid grid-cols-2 gap-4">
                    <Select label="Job Type" name="type" options={['Breakdown', 'Planned Service', 'Install', 'Audit']} />
                    <Select label="Priority" name="priority" options={['Low', 'Medium', 'High', 'Emergency']} />
                </div>
                <div>
                    <label className="block text-xs text-slate-400 uppercase font-bold mb-1">Description</label>
                    <textarea name="description" className="w-full bg-slate-900 border border-slate-700 text-white p-2 rounded focus:border-titan-500 outline-none h-24"></textarea>
                </div>
                <SubmitButton label="Create Job" />
            </form>
        </Modal>
    )
}

const AddCustomerModal = ({ onClose, onSave }: { onClose: () => void, onSave: (c: Customer) => void }) => {
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const formData = new FormData(e.target as HTMLFormElement);
        const newCust: Customer = {
            id: `CUST-${Date.now()}`,
            name: formData.get('name') as string,
            billingAddress: formData.get('address') as string,
            industry: formData.get('industry') as string,
            status: 'Active',
            activeSites: 0,
            type: 'Active',
            contacts: [],
            mainContact: { 
                name: formData.get('contactName') as string, 
                email: formData.get('contactEmail') as string, 
                phone: formData.get('contactPhone') as string 
            }
        };
        onSave(newCust);
    }
    return (
        <Modal title="Add Customer" onClose={onClose}>
             <form onSubmit={handleSubmit} className="space-y-4">
                <Input label="Customer Name" name="name" required />
                <Input label="Industry" name="industry" />
                <Input label="Billing Address" name="address" />
                <h4 className="text-white font-bold border-b border-slate-700 pb-2 mt-4">Primary Contact</h4>
                <Input label="Contact Name" name="contactName" required />
                <Input label="Email" name="contactEmail" type="email" required />
                <Input label="Phone" name="contactPhone" required />
                <SubmitButton label="Save Customer" />
            </form>
        </Modal>
    )
}

const AddSiteModal = ({ onClose, onSave }: { onClose: () => void, onSave: (s: Site) => void }) => {
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        // Simplified
        const newSite: Site = {
            id: `SITE-${Date.now()}`,
            customerId: 'CUST-001', // Mock
            name: 'New Site',
            address: '123 Test St',
            city: 'Durban',
            status: 'Active',
            contacts: [],
            contact: { name: 'Site Mgr', email: 'mgr@site.com', phone: '0821234567' },
            hsseRequirements: 'None'
        };
        onSave(newSite);
    }
    return (
        <Modal title="Add Site" onClose={onClose}>
             <form onSubmit={handleSubmit} className="space-y-4">
                 <div className="text-slate-500 italic">Form fields would go here...</div>
                 <SubmitButton label="Save Site" />
             </form>
        </Modal>
    )
}

const AddRentalAssetModal = ({ onClose, onSave }: { onClose: () => void, onSave: (a: RentalAsset) => void }) => {
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const formData = new FormData(e.target as HTMLFormElement);
        const newAsset: RentalAsset = {
            id: `RA-${Date.now()}`,
            assetId: 'UNK',
            fleetNumber: formData.get('fleet') as string,
            make: formData.get('make') as string,
            model: formData.get('model') as string,
            type: formData.get('type') as string,
            specs: formData.get('specs') as string,
            status: 'Available',
            depot: formData.get('depot') as string,
            dailyRate: Number(formData.get('rate')),
            weeklyRate: Number(formData.get('rate')) * 5,
            monthlyRate: Number(formData.get('rate')) * 20,
            bookValue: 0,
            purchaseDate: new Date().toISOString(),
            utilizationYTD: 0
        };
        onSave(newAsset);
    }
    return (
        <Modal title="Add Rental Asset" onClose={onClose}>
            <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                    <Input label="Fleet Number" name="fleet" required />
                    <Input label="Depot" name="depot" required />
                </div>
                <div className="grid grid-cols-2 gap-4">
                    <Input label="Make" name="make" required />
                    <Input label="Model" name="model" required />
                </div>
                <Input label="Type" name="type" placeholder="e.g. Diesel Portable" required />
                <Input label="Specs" name="specs" placeholder="e.g. 190CFM" />
                <Input label="Daily Rate (ZAR)" name="rate" type="number" required />
                <SubmitButton label="Add to Fleet" />
            </form>
        </Modal>
    )
}

const CreateContractModal = ({ onClose, onSave, preselectedAsset }: { onClose: () => void, onSave: (c: RentalContract) => void, preselectedAsset: RentalAsset | null }) => {
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const formData = new FormData(e.target as HTMLFormElement);
        const newContract: RentalContract = {
            id: `CTR-${Date.now()}`,
            customer: formData.get('customer') as string,
            site: formData.get('site') as string,
            startDate: formData.get('start') as string,
            endDate: formData.get('end') as string,
            status: 'Active',
            billingCycle: 'Monthly',
            assets: preselectedAsset ? [preselectedAsset] : []
        };
        onSave(newContract);
    }
    return (
        <Modal title="Create Rental Contract" onClose={onClose}>
             <form onSubmit={handleSubmit} className="space-y-4">
                 <Input label="Customer" name="customer" required />
                 <Input label="Site / Location" name="site" required />
                 <div className="grid grid-cols-2 gap-4">
                     <Input label="Start Date" name="start" type="date" required />
                     <Input label="Est. End Date" name="end" type="date" required />
                 </div>
                 {preselectedAsset && (
                     <div className="bg-slate-900 p-3 rounded border border-slate-700">
                         <div className="text-xs text-slate-500 uppercase font-bold">Asset to Dispatch</div>
                         <div className="text-white font-bold">{preselectedAsset.fleetNumber} - {preselectedAsset.make} {preselectedAsset.model}</div>
                     </div>
                 )}
                 <SubmitButton label="Create Contract & Dispatch" />
             </form>
        </Modal>
    )
}

// --- VIEWS ---

export const JobPlanningView: React.FC = () => {
  const [mode, setMode] = useState<'Planner' | 'Tech'>('Planner');
  const [plannerView, setPlannerView] = useState<'Kanban' | 'List'>('Kanban');
  const [jobs, setJobs] = useState<Job[]>(initialJobs);
  const [technicians] = useState<Technician[]>(initialTechnicians);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [showCreateJob, setShowCreateJob] = useState(false); 
  const [detailTab, setDetailTab] = useState<'Overview' | 'HSSE' | 'Execution' | 'Closeout'>('Overview');
  
  // Tech Flow State - Sub-tabs for Execution
  const [techExecTab, setTechExecTab] = useState<'Tasks' | 'Check20' | 'Quote' | 'Readings'>('Tasks');
  const [signature, setSignature] = useState<string>('');
  
  // Quote Request State
  const [showQuoteModal, setShowQuoteModal] = useState(false);

  // --- Handlers ---
  const handleOpenJob = (job: Job) => {
      setSelectedJob(job);
      setShowModal(true);
      setDetailTab('Overview');
  };

  const handleUpdateJob = (updates: Partial<Job>) => {
      if(!selectedJob) return;
      const updated = { ...selectedJob, ...updates };
      setJobs(jobs.map(j => j.id === updated.id ? updated : j));
      setSelectedJob(updated);
  }

  const handleSaveJob = () => {
      setShowModal(false);
      setSelectedJob(null);
  }

  const handleCreateJob = (newJob: Job) => {
      setJobs([...jobs, newJob]);
      setShowCreateJob(false);
  };

  // --- Tech Execution Handlers ---
  const addJobLog = (type: JobLog['type']) => {
      if(!selectedJob) return;
      const newLog: JobLog = {
          id: `L-${Date.now()}`,
          type,
          timestamp: new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}),
          user: 'Current User',
          gps: '-29.8587, 31.0218'
      };
      handleUpdateJob({ logs: [...selectedJob.logs, newLog] });
  }

  // --- STRICT FLOW HANDLERS ---
  
  const handleStartTravel = () => {
      addJobLog('Travel Start');
      handleUpdateJob({ 
          status: 'In Progress', // Using 'In Progress' as generic bucket, but relying on timestamps
          travelStartTime: new Date().toISOString()
      });
  }

  const handleArrive = () => {
      addJobLog('Arrived');
      handleUpdateJob({ arrivalTime: new Date().toISOString() });
  }

  const handleSubmitJHA = (riskLevel: 'Low' | 'Medium' | 'High') => {
      const ptwRequired = riskLevel === 'High';
      handleUpdateJob({
          riskLevel,
          ptwStatus: ptwRequired ? 'Draft' : 'Not Required',
          jhaCompleted: true
      });
  }

  const handleSubmitPTW = () => {
      handleUpdateJob({ ptwStatus: 'Pending' });
      // Simulate Approval Call for Demo
      setTimeout(() => {
          if(confirm("Simulate Office Approval for PTW?")) {
              handleUpdateJob({ ptwStatus: 'Approved' });
          }
      }, 2000);
  }

  const handleStartJob = () => {
      addJobLog('Work Start');
      handleUpdateJob({ jobStartTime: new Date().toISOString() });
  }

  const handleToggle20Point = (itemId: string, status: 'Pass' | 'Fail' | 'N/A') => {
      if(!selectedJob?.checklist20) return;
      const newChecklist = selectedJob.checklist20.map(item => 
          item.id === itemId ? { ...item, status } : item
      );
      handleUpdateJob({ checklist20: newChecklist });
  }

  const handleAddQuoteRequest = (e: React.FormEvent) => {
      e.preventDefault();
      const formData = new FormData(e.target as HTMLFormElement);
      const newQuote: QuoteRequest = {
          id: `QR-${Date.now()}`,
          description: formData.get('description') as string,
          photoUrl: 'mock_photo.jpg',
          status: 'Pending'
      };
      handleUpdateJob({ quoteRequests: [...(selectedJob?.quoteRequests || []), newQuote] });
      setShowQuoteModal(false);
  }

  const handleCompleteJob = () => {
      if (!signature) { alert("Customer signature required."); return; }
      addJobLog('Work End');
      handleUpdateJob({ 
          status: 'Completed', 
          jobEndTime: new Date().toISOString(),
          customerSignOff: 'Customer Rep',
          signatureUrl: 'signature_blob',
          completionDate: new Date().toISOString()
      });
      alert("Job Completed Successfully!");
      setSelectedJob(null);
  }

  const renderPlanner = () => (
      <div className="flex flex-col h-full">
          {/* Planner Header */}
          <div className="flex justify-between items-center mb-6 min-h-[4rem]">
              <h2 className="text-2xl font-bold text-white">Job Planning Board</h2>
              <div className="flex gap-3">
                  <div className="bg-slate-900 p-1 rounded border border-slate-700 flex">
                      <button onClick={() => setPlannerView('Kanban')} className={`px-3 py-1 text-sm rounded ${plannerView === 'Kanban' ? 'bg-slate-700 text-white' : 'text-slate-400'}`}><Layout className="w-4 h-4" /></button>
                      <button onClick={() => setPlannerView('List')} className={`px-3 py-1 text-sm rounded ${plannerView === 'List' ? 'bg-slate-700 text-white' : 'text-slate-400'}`}><List className="w-4 h-4" /></button>
                  </div>
                  <button onClick={() => setMode('Tech')} className="bg-slate-800 text-slate-300 hover:text-white px-4 py-2 rounded flex items-center gap-2 border border-slate-700">
                      <Smartphone className="w-4 h-4" /> Tech View
                  </button>
                  <button onClick={() => setShowCreateJob(true)} className="bg-titan-500 hover:bg-titan-600 text-white px-4 py-2 rounded font-bold flex items-center gap-2">
                      <Plus className="w-4 h-4" /> Create Job
                  </button>
              </div>
          </div>

          {/* Kanban Board */}
          {plannerView === 'Kanban' && (
              <div className="flex-1 overflow-x-auto pb-4">
                  <div className="flex gap-4 min-w-max h-full">
                      {['New', 'Planned', 'Allocated', 'In Progress', 'Completed'].map(status => (
                          <div key={status} className="w-80 bg-slate-900/50 rounded-lg border border-slate-800 flex flex-col max-h-full">
                              <div className="p-3 font-bold text-slate-400 uppercase text-xs flex justify-between border-b border-slate-800">
                                  {status}
                                  <span className="bg-slate-800 px-2 rounded text-white">{jobs.filter(j => j.status === status).length}</span>
                              </div>
                              <div className="p-2 space-y-2 flex-1 overflow-y-auto">
                                  {jobs.filter(j => j.status === status).map(job => (
                                      <div key={job.id} onClick={() => handleOpenJob(job)} className="bg-slate-800 p-3 rounded border border-slate-700 hover:border-titan-500 cursor-pointer group shadow-sm">
                                          <div className="flex justify-between items-start mb-2">
                                              <span className={`text-[10px] px-1.5 py-0.5 rounded uppercase font-bold ${job.priority === 'High' ? 'bg-red-500 text-white' : job.priority === 'Medium' ? 'bg-orange-500 text-white' : 'bg-blue-500 text-white'}`}>{job.priority}</span>
                                              <span className="text-xs font-mono text-slate-500">{job.id}</span>
                                          </div>
                                          <div className="text-white font-bold text-sm mb-1 truncate">{job.customer}</div>
                                          <div className="text-xs text-slate-400 mb-2 truncate">{job.site} • {job.machineName}</div>
                                          <div className="flex justify-between items-center text-xs border-t border-slate-700 pt-2 mt-2">
                                              <div className="flex items-center gap-1 text-slate-400">
                                                  <User className="w-3 h-3" /> {job.technician || 'Unassigned'}
                                              </div>
                                              <div className="text-slate-500">{job.dueDate}</div>
                                          </div>
                                      </div>
                                  ))}
                              </div>
                          </div>
                      ))}
                  </div>
              </div>
          )}

          {/* List View */}
          {plannerView === 'List' && (
              <div className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden flex-1">
                  <table className="w-full text-left">
                      <thead className="bg-slate-900 text-slate-400 text-xs uppercase">
                          <tr>
                              <th className="px-6 py-4">Job ID</th>
                              <th className="px-6 py-4">Customer</th>
                              <th className="px-6 py-4">Type</th>
                              <th className="px-6 py-4">Status</th>
                              <th className="px-6 py-4">Technician</th>
                              <th className="px-6 py-4">Due Date</th>
                              <th className="px-6 py-4">Action</th>
                          </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-700">
                          {jobs.map(job => (
                              <tr key={job.id} onClick={() => handleOpenJob(job)} className="hover:bg-slate-750 cursor-pointer">
                                  <td className="px-6 py-4 font-mono text-slate-400">{job.id}</td>
                                  <td className="px-6 py-4 font-bold text-white">
                                      {job.customer}
                                      <div className="text-xs text-slate-500 font-normal">{job.site}</div>
                                  </td>
                                  <td className="px-6 py-4 text-slate-300">{job.type}</td>
                                  <td className="px-6 py-4"><StatusBadge status={job.status} /></td>
                                  <td className="px-6 py-4 text-slate-300">{job.technician}</td>
                                  <td className="px-6 py-4 text-slate-400">{job.dueDate}</td>
                                  <td className="px-6 py-4 text-titan-500 hover:text-white text-sm font-bold">Manage</td>
                              </tr>
                          ))}
                      </tbody>
                  </table>
              </div>
          )}
      </div>
  );

  // --- Render Tech Mobile View ---
  const renderTechMobile = () => (
      <div className="max-w-md mx-auto h-full flex flex-col bg-slate-950 border-x border-slate-800 shadow-2xl relative">
          
          {/* Header */}
          <div className="bg-slate-900 p-4 border-b border-slate-800 flex justify-between items-center sticky top-0 z-10 shadow-lg">
              <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-titan-500 flex items-center justify-center font-bold text-white text-lg shadow-inner border border-titan-400">JS</div>
                  <div>
                      <div className="font-bold text-white leading-tight">John Smith</div>
                      <div className="text-xs text-green-400 flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div> Online</div>
                  </div>
              </div>
              <button onClick={() => setMode('Planner')} className="text-slate-500 hover:text-white p-2 bg-slate-800 rounded-full"><ArrowRightLeft className="w-5 h-5" /></button>
          </div>

          {/* JOB SELECTION LIST */}
          {!selectedJob ? (
              <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-950">
                  <div className="flex justify-between items-center mb-2">
                      <h3 className="text-slate-400 font-bold uppercase text-xs tracking-wider">Today's Schedule</h3>
                      <span className="bg-slate-800 text-slate-400 text-xs px-2 py-1 rounded-full">{jobs.filter(j => j.status === 'Allocated' && j.technician === 'J. Smith').length} Jobs</span>
                  </div>
                  
                  {jobs.filter(j => (j.status === 'Allocated' || j.status === 'In Progress') && j.technician === 'J. Smith').map(job => (
                      <div key={job.id} onClick={() => setSelectedJob(job)} className="bg-slate-900 p-4 rounded-xl border border-slate-800 active:scale-95 transition shadow-lg relative overflow-hidden group">
                          <div className={`absolute top-0 left-0 w-1.5 h-full ${job.priority === 'High' ? 'bg-red-500' : 'bg-blue-500'}`}></div>
                          <div className="flex justify-between items-start mb-3 pl-3">
                              <span className={`text-[10px] uppercase font-bold px-2 py-1 rounded shadow-sm ${job.priority === 'High' ? 'bg-red-600 text-white' : 'bg-blue-600 text-white'}`}>{job.priority}</span>
                              <span className="text-xs text-slate-500 font-mono">{job.id}</span>
                          </div>
                          <h4 className="text-white font-bold text-lg mb-1 pl-3">{job.customer}</h4>
                          <div className="text-slate-400 text-sm mb-4 flex items-center gap-1 pl-3"><MapPin className="w-4 h-4 text-titan-500"/> {job.site}</div>
                          <div className="flex gap-3 text-xs border-t border-slate-800 pt-3 pl-3">
                              <div className="flex-1 bg-slate-800 p-2 rounded text-center border border-slate-700">
                                  <div className="text-slate-500 mb-1 uppercase text-[10px] font-bold">Asset</div>
                                  <div className="text-white font-bold truncate">{job.machineName || 'N/A'}</div>
                              </div>
                              <div className="flex-1 bg-slate-800 p-2 rounded text-center border border-slate-700">
                                  <div className="text-slate-500 mb-1 uppercase text-[10px] font-bold">Type</div>
                                  <div className="text-white font-bold truncate">{job.type}</div>
                              </div>
                          </div>
                          <button className="mt-4 w-full bg-slate-800 hover:bg-slate-700 text-titan-500 border border-titan-500/30 py-3 rounded-lg font-bold text-center text-sm flex items-center justify-center gap-2 transition">
                              OPEN TICKET <ChevronRight className="w-4 h-4" />
                          </button>
                      </div>
                  ))}
              </div>
          ) : (
              // ACTIVE JOB WORKFLOW - STATE MACHINE UI
              <div className="flex-1 overflow-y-auto flex flex-col bg-slate-950">
                  {/* Job Sticky Header */}
                  <div className="p-3 bg-slate-900 border-b border-slate-800 sticky top-0 z-10 flex items-center gap-3 shadow-md">
                      <button onClick={() => setSelectedJob(null)} className="text-slate-400 hover:text-white p-2 rounded-full hover:bg-slate-800"><ArrowLeft className="w-5 h-5" /></button>
                      <div className="flex-1 overflow-hidden">
                          <h3 className="text-white font-bold truncate text-sm">{selectedJob.customer}</h3>
                          <div className="text-xs text-slate-400 flex items-center gap-2">
                              <span className="font-mono">{selectedJob.id}</span>
                              <span className="w-1 h-1 bg-slate-600 rounded-full"></span>
                              <span className="truncate">{selectedJob.site}</span>
                          </div>
                      </div>
                      <div className="text-xs font-bold px-2 py-1 bg-slate-800 rounded text-titan-500 border border-slate-700">
                          {selectedJob.jobStartTime ? 'EXECUTION' : selectedJob.arrivalTime ? 'SITE SAFETY' : selectedJob.travelStartTime ? 'TRAVEL' : 'PRE-START'}
                      </div>
                  </div>

                  <div className="flex-1 p-4 space-y-6">
                      
                      {/* --- STEP 1: TRAVEL --- */}
                      {!selectedJob.travelStartTime && (
                          <div className="space-y-6 text-center pt-8 animate-fade-in">
                              <div className="w-32 h-32 bg-slate-900 rounded-full flex items-center justify-center mx-auto mb-4 border-4 border-slate-800 shadow-[0_0_20px_rgba(0,0,0,0.5)]">
                                  <Navigation className="w-12 h-12 text-titan-500" />
                              </div>
                              <div>
                                  <h2 className="text-2xl font-bold text-white mb-2">Ready to Mobilize?</h2>
                                  <p className="text-slate-400 text-sm px-8">Confirm you are leaving your current location and heading to site.</p>
                              </div>
                              <button onClick={handleStartTravel} className="w-full bg-gradient-to-r from-titan-600 to-titan-500 hover:from-titan-500 hover:to-titan-400 text-white py-4 rounded-xl font-bold text-lg shadow-lg shadow-titan-900/50 flex items-center justify-center gap-3 transform active:scale-95 transition">
                                  <Navigation className="w-6 h-6" /> START TRAVEL
                              </button>
                              
                              <div className="bg-slate-900 p-4 rounded-lg text-left mt-8 border border-slate-800">
                                  <h4 className="text-xs text-slate-500 uppercase font-bold mb-2 flex items-center gap-2"><FileText className="w-3 h-3"/> Job Scope</h4>
                                  <p className="text-slate-300 text-sm">{selectedJob.description}</p>
                              </div>
                          </div>
                      )}

                      {/* --- STEP 2: ARRIVAL --- */}
                      {selectedJob.travelStartTime && !selectedJob.arrivalTime && (
                          <div className="space-y-6 text-center pt-8 animate-fade-in">
                              <div className="relative w-32 h-32 mx-auto mb-4">
                                  <div className="absolute inset-0 bg-blue-500/20 rounded-full animate-ping"></div>
                                  <div className="relative w-full h-full bg-slate-900 rounded-full flex items-center justify-center border-4 border-blue-500 shadow-[0_0_20px_rgba(59,130,246,0.3)]">
                                      <Truck className="w-12 h-12 text-blue-500" />
                                  </div>
                              </div>
                              <div>
                                  <h2 className="text-2xl font-bold text-white mb-2">En Route</h2>
                                  <p className="text-slate-400 text-sm">Tracking travel time...</p>
                                  <div className="mt-2 font-mono text-xl text-blue-400 font-bold">
                                      {/* Mock Timer */}
                                      00:45:12
                                  </div>
                              </div>
                              <button onClick={handleArrive} className="w-full bg-blue-600 hover:bg-blue-500 text-white py-4 rounded-xl font-bold text-lg shadow-lg shadow-blue-900/30 flex items-center justify-center gap-3 transform active:scale-95 transition">
                                  <MapPin className="w-6 h-6" /> I HAVE ARRIVED
                              </button>
                          </div>
                      )}

                      {/* --- STEP 3: HSSE GATE (JHA & PTW) --- */}
                      {selectedJob.arrivalTime && !selectedJob.jobStartTime && (
                          <div className="space-y-4 animate-fade-in">
                              
                              {/* JHA Section */}
                              <div className={`p-4 rounded-xl border transition-all duration-300 ${selectedJob.jhaCompleted ? 'bg-green-900/10 border-green-900/30' : 'bg-slate-900 border-slate-800'}`}>
                                  <div className="flex justify-between items-center mb-4">
                                      <h3 className="font-bold text-white flex items-center gap-2">
                                          <Shield className={`w-5 h-5 ${selectedJob.jhaCompleted ? 'text-green-500' : 'text-slate-400'}`} />
                                          1. Job Hazard Analysis
                                      </h3>
                                      {selectedJob.jhaCompleted && <CheckCircle className="w-5 h-5 text-green-500" />}
                                  </div>
                                  
                                  {!selectedJob.jhaCompleted ? (
                                      <div className="space-y-4">
                                          <p className="text-sm text-slate-400">Assess site hazards to determine risk level.</p>
                                          <div className="grid grid-cols-2 gap-3">
                                              {['Heights', 'Electrical', 'Hot Work', 'Pressure', 'Chemicals', 'Moving Parts'].map(h => (
                                                  <label key={h} className="flex items-center gap-2 p-3 bg-slate-950 rounded border border-slate-800">
                                                      <input type="checkbox" className="w-4 h-4 rounded bg-slate-800 border-slate-600 text-titan-500" />
                                                      <span className="text-sm text-slate-300">{h}</span>
                                                  </label>
                                              ))}
                                          </div>
                                          <div className="pt-2">
                                              <label className="block text-xs text-slate-500 uppercase font-bold mb-2">Calculated Risk Level</label>
                                              <div className="grid grid-cols-3 gap-2">
                                                  {['Low', 'Medium', 'High'].map(level => (
                                                      <button 
                                                        key={level}
                                                        onClick={() => handleSubmitJHA(level as any)}
                                                        className={`py-3 rounded font-bold text-sm border-2 transition hover:opacity-80
                                                            ${level === 'Low' ? 'border-green-600 text-green-500 bg-green-900/10' : 
                                                              level === 'Medium' ? 'border-yellow-600 text-yellow-500 bg-yellow-900/10' : 
                                                              'border-red-600 text-red-500 bg-red-900/10'}`}
                                                      >
                                                          {level}
                                                      </button>
                                                  ))}
                                              </div>
                                          </div>
                                      </div>
                                  ) : (
                                      <div className="flex justify-between items-center text-sm">
                                          <span className="text-slate-400">Risk Assessment Complete</span>
                                          <span className={`font-bold px-2 py-1 rounded text-xs ${selectedJob.riskLevel === 'High' ? 'bg-red-500 text-white' : 'bg-green-500 text-white'}`}>
                                              {selectedJob.riskLevel} Risk
                                          </span>
                                      </div>
                                  )}
                              </div>

                              {/* PTW Section (Conditional) */}
                              {selectedJob.riskLevel === 'High' && (
                                  <div className={`p-4 rounded-xl border transition-all duration-300 ${selectedJob.ptwStatus === 'Approved' ? 'bg-green-900/10 border-green-900/30' : 'bg-slate-900 border-slate-800'}`}>
                                      <div className="flex justify-between items-center mb-4">
                                          <h3 className="font-bold text-white flex items-center gap-2">
                                              <Lock className={`w-5 h-5 ${selectedJob.ptwStatus === 'Approved' ? 'text-green-500' : 'text-red-500'}`} />
                                              2. Permit to Work (High Risk)
                                          </h3>
                                          {selectedJob.ptwStatus === 'Approved' && <CheckCircle className="w-5 h-5 text-green-500" />}
                                      </div>

                                      {selectedJob.ptwStatus !== 'Approved' && (
                                          <div className="space-y-4">
                                              <div className="bg-red-900/20 p-3 rounded border border-red-900/50 text-red-300 text-sm flex items-center gap-2">
                                                  <AlertTriangle className="w-4 h-4 shrink-0" />
                                                  High Risk work requires approved permit before starting.
                                              </div>
                                              
                                              {selectedJob.ptwStatus === 'Draft' && (
                                                  <button onClick={handleSubmitPTW} className="w-full bg-red-600 hover:bg-red-500 text-white py-3 rounded-lg font-bold shadow-lg">
                                                      Submit Permit Request
                                                  </button>
                                              )}
                                              
                                              {selectedJob.ptwStatus === 'Pending' && (
                                                  <div className="text-center py-4">
                                                      <div className="animate-spin w-8 h-8 border-4 border-titan-500 border-t-transparent rounded-full mx-auto mb-2"></div>
                                                      <div className="text-titan-500 font-bold">Waiting for Office Approval...</div>
                                                      <div className="text-xs text-slate-500">Notification sent to Safety Officer</div>
                                                  </div>
                                              )}
                                          </div>
                                      )}
                                      
                                      {selectedJob.ptwStatus === 'Approved' && (
                                          <div className="text-sm text-green-400">Permit #PTW-992 authorized. You may proceed.</div>
                                      )}
                                  </div>
                              )}

                              {/* Start Job Gate */}
                              <div className="pt-4">
                                  {selectedJob.jhaCompleted && (selectedJob.riskLevel !== 'High' || selectedJob.ptwStatus === 'Approved') ? (
                                      <button onClick={handleStartJob} className="w-full bg-green-600 hover:bg-green-500 text-white py-4 rounded-xl font-bold text-lg shadow-lg shadow-green-900/30 flex items-center justify-center gap-3 animate-pulse">
                                          <Wrench className="w-6 h-6" /> START JOB
                                      </button>
                                  ) : (
                                      <button disabled className="w-full bg-slate-800 text-slate-500 py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-3 cursor-not-allowed border border-slate-700">
                                          <Lock className="w-5 h-5" /> Safety Checks Required
                                      </button>
                                  )}
                              </div>
                          </div>
                      )}

                      {/* --- STEP 4: EXECUTION --- */}
                      {selectedJob.jobStartTime && !selectedJob.jobEndTime && (
                          <div className="h-full flex flex-col animate-fade-in">
                              {/* Execution Tabs */}
                              <div className="flex bg-slate-900 p-1 rounded-lg mb-4 border border-slate-800">
                                  {[
                                      {id: 'Tasks', icon: List, label: 'Scope'},
                                      {id: 'Check20', icon: ClipboardCheck, label: '20-Pt Check'},
                                      {id: 'Quote', icon: DollarSign, label: 'Extra'},
                                      {id: 'Readings', icon: Gauge, label: 'Data'},
                                  ].map(t => (
                                      <button 
                                        key={t.id}
                                        onClick={() => setTechExecTab(t.id as any)}
                                        className={`flex-1 py-2 rounded-md flex flex-col items-center justify-center text-xs gap-1 transition ${techExecTab === t.id ? 'bg-slate-800 text-white shadow font-bold' : 'text-slate-500 hover:text-slate-300'}`}
                                      >
                                          <t.icon className="w-4 h-4" /> {t.label}
                                      </button>
                                  ))}
                              </div>

                              <div className="flex-1 overflow-y-auto pb-20">
                                  
                                  {techExecTab === 'Tasks' && (
                                      <div className="space-y-4">
                                          <div className="bg-slate-900 p-4 rounded-lg border border-slate-800">
                                              <h4 className="text-white font-bold mb-2">Scope of Work</h4>
                                              <p className="text-sm text-slate-300">{selectedJob.description}</p>
                                          </div>
                                          
                                          <div className="space-y-2">
                                              <h4 className="text-xs text-slate-500 uppercase font-bold pl-1">Standard Tasks</h4>
                                              {['Isolate Unit', 'Replace Oil Filter', 'Replace Air Filter', 'Check Belts', 'Clean Cooler'].map((task, i) => (
                                                  <label key={i} className="flex items-center gap-3 p-3 bg-slate-900 rounded-lg border border-slate-800 active:bg-slate-800 transition">
                                                      <input type="checkbox" className="w-5 h-5 rounded bg-slate-950 border-slate-600 text-titan-500" />
                                                      <span className="text-slate-200 text-sm font-medium">{task}</span>
                                                  </label>
                                              ))}
                                          </div>
                                      </div>
                                  )}

                                  {techExecTab === 'Check20' && (
                                      <div className="space-y-3">
                                          <div className="flex justify-between items-center px-1 mb-2">
                                              <h4 className="text-white font-bold text-sm">Standard 20-Point Check</h4>
                                              <span className="text-xs text-titan-500">{selectedJob.checklist20?.filter(c => c.status === 'Pass').length}/20 Passed</span>
                                          </div>
                                          {selectedJob.checklist20?.map((item) => (
                                              <div key={item.id} className="bg-slate-900 p-3 rounded-lg border border-slate-800 flex justify-between items-center">
                                                  <span className="text-sm text-slate-200">{item.label}</span>
                                                  <div className="flex gap-1">
                                                      <button 
                                                        onClick={() => handleToggle20Point(item.id, 'Pass')}
                                                        className={`p-2 rounded transition ${item.status === 'Pass' ? 'bg-green-600 text-white' : 'bg-slate-800 text-slate-600 hover:bg-slate-700'}`}
                                                      >
                                                          <ThumbsUp className="w-4 h-4" />
                                                      </button>
                                                      <button 
                                                        onClick={() => handleToggle20Point(item.id, 'Fail')}
                                                        className={`p-2 rounded transition ${item.status === 'Fail' ? 'bg-red-600 text-white' : 'bg-slate-800 text-slate-600 hover:bg-slate-700'}`}
                                                      >
                                                          <ThumbsDown className="w-4 h-4" />
                                                      </button>
                                                      <button 
                                                        onClick={() => handleToggle20Point(item.id, 'N/A')}
                                                        className={`p-2 rounded transition text-xs font-bold w-8 ${item.status === 'N/A' ? 'bg-slate-600 text-white' : 'bg-slate-800 text-slate-600 hover:bg-slate-700'}`}
                                                      >
                                                          N/A
                                                      </button>
                                                  </div>
                                              </div>
                                          ))}
                                      </div>
                                  )}

                                  {techExecTab === 'Quote' && (
                                      <div className="space-y-4">
                                          <div className="text-center py-6 bg-slate-900 rounded-lg border border-slate-800 border-dashed">
                                              <DollarSign className="w-8 h-8 text-titan-500 mx-auto mb-2" />
                                              <h4 className="text-white font-bold">Extra Work Found?</h4>
                                              <p className="text-xs text-slate-400 mb-4 px-4">Don't perform work outside scope without approval. Raise a quote request instantly.</p>
                                              <button onClick={() => setShowQuoteModal(true)} className="bg-titan-500 text-white px-6 py-2 rounded-full font-bold text-sm shadow-lg">
                                                  + Request Quote
                                              </button>
                                          </div>

                                          <div className="space-y-3">
                                              <h4 className="text-xs text-slate-500 uppercase font-bold pl-1">Requests Logged</h4>
                                              {selectedJob.quoteRequests && selectedJob.quoteRequests.length > 0 ? selectedJob.quoteRequests.map(q => (
                                                  <div key={q.id} className="bg-slate-900 p-3 rounded-lg border border-slate-800 flex justify-between items-center">
                                                      <div>
                                                          <div className="text-white text-sm font-medium">{q.description}</div>
                                                          <div className="text-xs text-slate-500">{q.id} • {q.status}</div>
                                                      </div>
                                                      {q.status === 'Pending' ? <span className="w-2 h-2 rounded-full bg-yellow-500"></span> : <span className="w-2 h-2 rounded-full bg-green-500"></span>}
                                                  </div>
                                              )) : (
                                                  <div className="text-center text-slate-600 text-sm italic py-4">No quotes requested yet.</div>
                                              )}
                                          </div>
                                      </div>
                                  )}

                                  {techExecTab === 'Readings' && (
                                      <div className="space-y-4">
                                          <div className="grid grid-cols-2 gap-4">
                                              <Input label="Running Hours" type="number" />
                                              <Input label="Load Hours" type="number" />
                                          </div>
                                          <div className="grid grid-cols-2 gap-4">
                                              <Input label="Delivery Pressure (Bar)" type="number" />
                                              <Input label="Delivery Temp (°C)" type="number" />
                                          </div>
                                          <div className="grid grid-cols-2 gap-4">
                                              <Input label="Oil Pressure (Bar)" type="number" />
                                              <Input label="Motor Amps (A)" type="number" />
                                          </div>
                                      </div>
                                  )}
                              </div>

                              {/* Complete Button Footer */}
                              <div className="absolute bottom-0 left-0 right-0 p-4 bg-slate-950 border-t border-slate-800">
                                  <button onClick={() => setTechExecTab('Check20')} className="w-full bg-blue-600 hover:bg-blue-500 text-white py-4 rounded-xl font-bold text-lg shadow-lg flex items-center justify-center gap-3">
                                      <CheckSquare className="w-6 h-6" /> PROCEED TO CLOSE
                                  </button>
                              </div>
                          </div>
                      )}

                      {/* --- STEP 5: CLOSEOUT --- */}
                      {selectedJob.jobStartTime && selectedJob.jobEndTime && (
                          <div className="space-y-6 animate-fade-in text-center pt-8">
                              <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-4" />
                              <h2 className="text-2xl font-bold text-white">Job Done!</h2>
                              <p className="text-slate-400 text-sm mb-6">Verify details and capture customer signature.</p>
                              
                              <div className="bg-slate-900 p-4 rounded-lg border border-slate-800 text-left space-y-4">
                                  <Input label="Customer Name" placeholder="e.g. Peter Jones" />
                                  <Input label="Email for Report" placeholder="peter@client.com" />
                                  
                                  <div>
                                      <label className="block text-xs text-slate-400 uppercase font-bold mb-2">Signature</label>
                                      <div className="bg-white h-32 rounded border-2 border-dashed border-slate-500 flex items-center justify-center relative overflow-hidden">
                                          {signature ? (
                                              <div className="text-black font-script text-3xl italic">Signed by Customer</div>
                                          ) : (
                                              <div 
                                                className="absolute inset-0 cursor-crosshair active:bg-gray-100" 
                                                onClick={() => setSignature('Signed')} // Mock signing
                                              >
                                                  <div className="flex items-center justify-center h-full text-slate-400 text-sm">
                                                      Tap to Sign Here
                                                  </div>
                                              </div>
                                          )}
                                      </div>
                                      {signature && <button onClick={() => setSignature('')} className="text-xs text-red-400 mt-2 underline">Clear Signature</button>}
                                  </div>
                              </div>

                              <button onClick={handleCompleteJob} className="w-full bg-green-600 hover:bg-green-500 text-white py-4 rounded-xl font-bold text-lg shadow-lg flex items-center justify-center gap-2">
                                  <LogOut className="w-6 h-6" /> SUBMIT & CLOSE
                              </button>
                          </div>
                      )}

                  </div>
              </div>
          )}

          {/* Quote Modal */}
          {showQuoteModal && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 animate-fade-in">
                  <div className="bg-slate-900 rounded-lg w-full max-w-sm border border-slate-700 p-6 space-y-4">
                      <h3 className="text-white font-bold text-lg">Request Extra Work Quote</h3>
                      <div>
                          <label className="block text-xs text-slate-400 uppercase font-bold mb-1">Description of Issue</label>
                          <textarea name="description" className="w-full bg-slate-800 border border-slate-600 text-white p-3 rounded h-32 outline-none focus:border-titan-500" placeholder="Describe what needs to be quoted..."></textarea>
                      </div>
                      <div className="border-2 border-dashed border-slate-700 rounded p-4 text-center text-slate-500">
                          <Camera className="w-6 h-6 mx-auto mb-2" />
                          Tap to Add Photo
                      </div>
                      <div className="flex gap-3 pt-2">
                          <button onClick={() => setShowQuoteModal(false)} className="flex-1 bg-slate-800 text-slate-300 py-3 rounded font-bold">Cancel</button>
                          <button onClick={() => { setShowQuoteModal(false); /* In real app, submit form */ }} className="flex-1 bg-titan-500 text-white py-3 rounded font-bold">Submit</button>
                      </div>
                  </div>
              </div>
          )}
      </div>
  );

  return (
    <div className="h-full animate-fade-in">
        {mode === 'Planner' ? (
            <>
                {renderPlanner()}
                
                {/* --- JOB DETAIL MODAL (PLANNER) --- */}
                {showModal && selectedJob && (
                    <Modal title={`Job Management: ${selectedJob.id}`} onClose={handleSaveJob} size="xl">
                        <div className="flex flex-col h-[700px]">
                            {/* Header Summary */}
                            <div className="flex justify-between items-start bg-slate-900 p-4 rounded border border-slate-700 mb-6">
                                <div>
                                    <h2 className="text-2xl font-bold text-white mb-1">{selectedJob.customer}</h2>
                                    <div className="flex gap-4 text-sm text-slate-400">
                                        <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {selectedJob.site}</span>
                                        <span className="flex items-center gap-1"><Wrench className="w-3 h-3" /> {selectedJob.machineName || 'General'}</span>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <StatusBadge status={selectedJob.status} />
                                    <div className="mt-2 text-xs text-slate-500">Created: {selectedJob.dateCreated}</div>
                                </div>
                            </div>

                            {/* Tabs */}
                            <div className="flex border-b border-slate-700 mb-4">
                                {['Overview', 'HSSE', 'Execution', 'Closeout'].map(t => (
                                    <button 
                                      key={t}
                                      onClick={() => setDetailTab(t as any)}
                                      className={`px-6 py-3 font-bold text-sm transition border-b-2 ${detailTab === t ? 'border-titan-500 text-titan-500 bg-slate-800' : 'border-transparent text-slate-400 hover:text-white hover:bg-slate-800'}`}
                                    >
                                        {t}
                                    </button>
                                ))}
                            </div>

                            {/* Tab Content */}
                            <div className="flex-1 overflow-y-auto pr-2">
                                {detailTab === 'Overview' && (
                                    <div className="space-y-6 animate-fade-in">
                                        <div className="grid grid-cols-2 gap-6">
                                            <div>
                                                <h4 className="text-titan-500 font-bold text-sm uppercase mb-3 border-b border-slate-700 pb-1">Scope & Planning</h4>
                                                <div className="space-y-4">
                                                    <div className="grid grid-cols-2 gap-4">
                                                        <Input label="Job Type" value={selectedJob.type} disabled />
                                                        <Select label="Priority" defaultValue={selectedJob.priority} options={['Low', 'Medium', 'High', 'Emergency']} />
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs text-slate-400 uppercase font-bold mb-1">Detailed Scope</label>
                                                        <textarea className="w-full bg-slate-900 border border-slate-700 text-white p-2 rounded focus:border-titan-500 outline-none h-24" defaultValue={selectedJob.description}></textarea>
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-4">
                                                        <Input label="Planned Start" type="datetime-local" />
                                                        <Input label="Est. Duration (Hrs)" type="number" />
                                                    </div>
                                                </div>
                                            </div>
                                            <div>
                                                <h4 className="text-titan-500 font-bold text-sm uppercase mb-3 border-b border-slate-700 pb-1">Allocation</h4>
                                                <div className="bg-slate-900 p-4 rounded border border-slate-700 space-y-4">
                                                    <div>
                                                        <label className="block text-xs text-slate-400 uppercase font-bold mb-1">Primary Technician</label>
                                                        <select className="w-full bg-slate-800 border border-slate-600 text-white p-2 rounded focus:border-titan-500 outline-none" value={selectedJob.technician} onChange={(e) => handleUpdateJob({technician: e.target.value, status: 'Allocated'})}>
                                                            <option value="Unassigned">Unassigned</option>
                                                            {technicians.map(t => (
                                                                <option key={t.id} value={t.name}>{t.name} ({t.status})</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs text-slate-400 uppercase font-bold mb-1">Assistant(s)</label>
                                                        <div className="bg-slate-800 border border-slate-600 p-2 rounded max-h-24 overflow-y-auto">
                                                            {technicians.map(t => (
                                                                <label key={t.id} className="flex items-center gap-2 p-1 hover:bg-slate-700 rounded cursor-pointer">
                                                                    <input type="checkbox" className="rounded bg-slate-900 border-slate-500 text-titan-500" />
                                                                    <span className="text-sm text-slate-300">{t.name}</span>
                                                                </label>
                                                            ))}
                                                        </div>
                                                    </div>
                                                    <div className="text-xs text-slate-500 pt-2 italic">
                                                        Assigning a technician will notify them via the mobile app immediately.
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {detailTab === 'HSSE' && (
                                    <div className="space-y-6 animate-fade-in">
                                        <div className="bg-red-900/10 border border-red-900/50 p-4 rounded mb-6 flex gap-4 items-start">
                                            <AlertTriangle className="w-6 h-6 text-red-500 shrink-0" />
                                            <div>
                                                <h4 className="text-red-400 font-bold">Site Requirements</h4>
                                                <p className="text-sm text-red-200">Red Ticket Required. Zero Tolerance on Alcohol. Mandatory Induction Video before entry.</p>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 gap-6">
                                            <div className="bg-slate-900 p-4 rounded border border-slate-700">
                                                <h4 className="text-white font-bold mb-3">Permits Required</h4>
                                                <div className="space-y-2">
                                                    <label className="flex items-center gap-3 p-2 hover:bg-slate-800 rounded cursor-pointer">
                                                        <input type="checkbox" className="w-4 h-4 rounded bg-slate-900 border-slate-600 text-titan-500" />
                                                        <span className="text-slate-300">Hot Work Permit</span>
                                                    </label>
                                                    <label className="flex items-center gap-3 p-2 hover:bg-slate-800 rounded cursor-pointer">
                                                        <input type="checkbox" className="w-4 h-4 rounded bg-slate-900 border-slate-600 text-titan-500" />
                                                        <span className="text-slate-300">Confined Space</span>
                                                    </label>
                                                    <label className="flex items-center gap-3 p-2 hover:bg-slate-800 rounded cursor-pointer">
                                                        <input type="checkbox" className="w-4 h-4 rounded bg-slate-900 border-slate-600 text-titan-500" />
                                                        <span className="text-slate-300">Working at Heights</span>
                                                    </label>
                                                </div>
                                            </div>
                                            <div className="bg-slate-900 p-4 rounded border border-slate-700">
                                                <h4 className="text-white font-bold mb-3">Tech Compliance</h4>
                                                <div className="space-y-3">
                                                    <div className="flex justify-between text-sm">
                                                        <span className="text-slate-400">JHA Status</span>
                                                        <span className={`${selectedJob.jhaCompleted ? 'text-green-500' : 'text-yellow-500'} font-bold`}>{selectedJob.jhaCompleted ? 'Completed' : 'Pending'}</span>
                                                    </div>
                                                    <div className="flex justify-between text-sm">
                                                        <span className="text-slate-400">Tech Certifications</span>
                                                        <span className="text-green-500 font-bold flex items-center gap-1"><CheckCircle className="w-3 h-3"/> Valid</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {detailTab === 'Execution' && (
                                    <div className="space-y-6 animate-fade-in">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            {/* Parts Used */}
                                            <div className="bg-slate-900 p-4 rounded border border-slate-700">
                                                <div className="flex justify-between items-center mb-3">
                                                    <h4 className="text-white font-bold flex items-center gap-2"><Box className="w-4 h-4 text-titan-500"/> Parts & Materials</h4>
                                                    <button className="text-xs bg-slate-800 hover:bg-slate-700 text-white px-2 py-1 rounded border border-slate-600">+ Add Part</button>
                                                </div>
                                                <div className="text-center text-slate-500 italic text-sm py-4">No parts recorded yet.</div>
                                            </div>

                                            {/* Time Logs */}
                                            <div className="bg-slate-900 p-4 rounded border border-slate-700">
                                                <h4 className="text-white font-bold mb-3 flex items-center gap-2"><Clock className="w-4 h-4 text-blue-500"/> Time Logs</h4>
                                                <div className="space-y-2 text-sm">
                                                    {selectedJob.logs.length > 0 ? selectedJob.logs.map(log => (
                                                        <div key={log.id} className="flex justify-between text-slate-300 border-b border-slate-800 pb-1">
                                                            <span>{log.type}</span>
                                                            <span className="font-mono text-slate-500">{log.timestamp}</span>
                                                        </div>
                                                    )) : (
                                                        <div className="text-slate-500 italic">No logs yet.</div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Checklist Preview */}
                                        <div className="bg-slate-900 p-4 rounded border border-slate-700">
                                            <h4 className="text-white font-bold mb-3 flex items-center gap-2"><ClipboardCheck className="w-4 h-4 text-green-500"/> 20-Point Checklist Result</h4>
                                            <div className="grid grid-cols-2 gap-2">
                                                {selectedJob.checklist20?.map((i, idx) => (
                                                    <div key={idx} className="bg-slate-800 px-3 py-2 rounded text-sm flex justify-between items-center">
                                                        <span className="text-slate-300">{i.label}</span>
                                                        <span className={`text-xs font-bold px-2 py-0.5 rounded ${i.status === 'Pass' ? 'bg-green-900 text-green-400' : i.status === 'Fail' ? 'bg-red-900 text-red-400' : 'bg-slate-700 text-slate-500'}`}>{i.status}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {detailTab === 'Closeout' && (
                                    <div className="space-y-6 animate-fade-in">
                                        {selectedJob.status === 'Completed' ? (
                                            <div className="bg-slate-900 p-6 rounded border border-slate-700 text-center">
                                                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                                                <h3 className="text-xl font-bold text-white mb-2">Job Completed by Tech</h3>
                                                <p className="text-slate-400 mb-6">Technician has signed off. Please review checklist and logs before closing the job for invoicing.</p>
                                                
                                                <div className="flex justify-center gap-4">
                                                    <button 
                                                        onClick={() => { handleUpdateJob({status: 'In Progress'}); alert("Job reopened for technician."); }}
                                                        className="px-6 py-2 bg-red-600/20 text-red-400 border border-red-600/50 rounded font-bold hover:bg-red-600 hover:text-white transition"
                                                    >
                                                        Reject / Re-open
                                                    </button>
                                                    <button 
                                                        onClick={() => { handleUpdateJob({status: 'Closed'}); alert("Job Closed. Sent to Invoicing."); setShowModal(false); }}
                                                        className="px-6 py-2 bg-green-600 hover:bg-green-500 text-white rounded font-bold transition shadow-lg shadow-green-900/20"
                                                    >
                                                        Approve & Close
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="text-center py-10">
                                                <FileCheck className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                                                <h3 className="text-xl font-bold text-slate-300">Job Not Ready for Close-out</h3>
                                                <p className="text-slate-500 max-w-md mx-auto">
                                                    The technician has not marked this job as complete yet. 
                                                    Current Status: <span className="text-titan-500 font-bold">{selectedJob.status}</span>
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Footer Actions */}
                            <div className="pt-4 border-t border-slate-700 flex justify-end gap-3 mt-4">
                                <button onClick={() => setShowModal(false)} className="px-4 py-2 text-slate-400 hover:text-white">Close</button>
                                <button onClick={handleSaveJob} className="bg-titan-500 hover:bg-titan-600 text-white px-6 py-2 rounded font-bold shadow-lg">Save & Update</button>
                            </div>
                        </div>
                    </Modal>
                )}

                {/* --- CREATE JOB MODAL (PLANNER) --- */}
                {showCreateJob && (
                    <CreateJobModal 
                        onClose={() => setShowCreateJob(false)}
                        onSave={handleCreateJob}
                    />
                )}
            </>
        ) : (
            renderTechMobile()
        )}
    </div>
  );
};

export const ServiceCRMView: React.FC = () => {
    // ... [Keep existing implementation]
    const [view, setView] = useState<'Customers' | 'Sites'>('Customers');
    const [customers, setCustomers] = useState<Customer[]>(initialCustomers);
    const [sites, setSites] = useState<Site[]>(initialSites);
    const [showAddCustomer, setShowAddCustomer] = useState(false);
    const [showAddSite, setShowAddSite] = useState(false);

    // Handlers
    const handleAddCustomer = (customer: Customer) => {
        setCustomers([...customers, customer]);
        setShowAddCustomer(false);
    };

    const handleAddSite = (site: Site) => {
        setSites([...sites, site]);
        setShowAddSite(false);
    };

    return (
        <div className="animate-fade-in">
             <div className="flex justify-between items-center mb-6 min-h-[4rem]">
                <h2 className="text-2xl font-bold text-white">Service CRM</h2>
                <div className="flex items-center gap-4">
                    <div className="bg-slate-900 p-1 rounded border border-slate-700 flex">
                        <button onClick={() => setView('Customers')} className={`px-3 py-1 text-sm rounded ${view === 'Customers' ? 'bg-slate-700 text-white' : 'text-slate-400'}`}>Customers</button>
                        <button onClick={() => setView('Sites')} className={`px-3 py-1 text-sm rounded ${view === 'Sites' ? 'bg-slate-700 text-white' : 'text-slate-400'}`}>Sites</button>
                    </div>
                    {view === 'Customers' && (
                        <button onClick={() => setShowAddCustomer(true)} className="bg-titan-500 hover:bg-titan-600 text-white px-4 py-2 rounded font-bold flex items-center gap-2">
                           <Plus className="w-4 h-4" /> Add Customer
                       </button>
                    )}
                    {view === 'Sites' && (
                        <button onClick={() => setShowAddSite(true)} className="bg-titan-500 hover:bg-titan-600 text-white px-4 py-2 rounded font-bold flex items-center gap-2">
                           <Plus className="w-4 h-4" /> Add Site
                       </button>
                    )}
                </div>
            </div>

            {view === 'Customers' && (
                <div className="space-y-4">

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {customers.map(c => (
                            <div key={c.id} className="bg-slate-800 p-5 rounded-lg border border-slate-700 hover:border-titan-500 transition cursor-pointer">
                                <div className="flex justify-between items-start mb-2">
                                     <div className="font-bold text-white text-lg">{c.name}</div>
                                     <StatusBadge status={c.status} />
                                </div>
                                <div className="text-sm text-slate-400 mb-4">{c.industry}</div>
                                <div className="space-y-2 text-sm border-t border-slate-700 pt-3">
                                    <div className="flex items-center gap-2 text-slate-300">
                                        <User className="w-4 h-4 text-titan-500" /> {c.mainContact.name}
                                    </div>
                                    <div className="flex items-center gap-2 text-slate-300">
                                        <Mail className="w-4 h-4 text-titan-500" /> {c.mainContact.email}
                                    </div>
                                    <div className="flex items-center gap-2 text-slate-300">
                                        <Phone className="w-4 h-4 text-titan-500" /> {c.mainContact.phone}
                                    </div>
                                    <div className="flex items-center gap-2 text-slate-300">
                                        <MapPin className="w-4 h-4 text-titan-500" /> {c.activeSites} Sites
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {view === 'Sites' && (
                <div className="space-y-4">

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {sites.map(s => (
                            <div key={s.id} className="bg-slate-800 p-5 rounded-lg border border-slate-700">
                                <div className="flex justify-between items-start mb-2">
                                     <div className="font-bold text-white text-lg">{s.name}</div>
                                     <StatusBadge status={s.status} />
                                </div>
                                <div className="text-sm text-slate-500 mb-1">{customers.find(c => c.id === s.customerId)?.name}</div>
                                <div className="text-sm text-slate-400 mb-4">{s.city}</div>
                                <div className="space-y-2 text-sm border-t border-slate-700 pt-3">
                                    <div className="flex items-center gap-2 text-slate-300">
                                        <User className="w-4 h-4 text-titan-500" /> {s.contact.name}
                                    </div>
                                    <div className="flex items-center gap-2 text-slate-300">
                                        <Phone className="w-4 h-4 text-titan-500" /> {s.contact.phone}
                                    </div>
                                     <div className="flex items-center gap-2 text-slate-300">
                                        <MapPin className="w-4 h-4 text-titan-500" /> {s.address}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {showAddCustomer && <AddCustomerModal onClose={() => setShowAddCustomer(false)} onSave={handleAddCustomer} />}
            {showAddSite && <AddSiteModal onClose={() => setShowAddSite(false)} onSave={handleAddSite} />}
        </div>
    );
};

export const AssetManagerView: React.FC = () => {
    // ... (Keep existing implementation)
    const [assets] = useState<Asset[]>(initialAssets);
    return (
        <div className="animate-fade-in">
             <div className="flex justify-between items-center mb-6 min-h-[4rem]">
                <h2 className="text-2xl font-bold text-white">Asset Manager</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {assets.map(asset => (
                    <div key={asset.id} className="bg-slate-800 p-5 rounded-lg border border-slate-700">
                        <div className="flex justify-between items-start mb-2">
                             <div className="font-mono text-titan-500 font-bold">{asset.tag}</div>
                             <div className={`text-xs px-2 py-1 rounded ${asset.status === 'Active' ? 'bg-green-900/30 text-green-400' : 'bg-red-900/30 text-red-400'}`}>{asset.status}</div>
                        </div>
                        <div className="text-white font-medium text-lg">{asset.make} {asset.model}</div>
                        <div className="text-sm text-slate-400 mb-4">{asset.category}</div>
                        <div className="text-sm text-slate-500 space-y-1 border-t border-slate-700 pt-3">
                            <div className="flex justify-between"><span>Site:</span> <span className="text-slate-300">{asset.location}</span></div>
                            <div className="flex justify-between"><span>Hours:</span> <span className="text-slate-300">{asset.hoursReading}</span></div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    )
}

export const RentalManagerView: React.FC = () => {
    // ... (Keep existing implementation)
    const [tab, setTab] = useState<'Dashboard' | 'Fleet' | 'Contracts' | 'Inspections'>('Dashboard');
    const [fleet, setFleet] = useState<RentalAsset[]>(initialRentalFleet);
    const [contracts, setContracts] = useState<RentalContract[]>(initialContracts);
    const [showInspectionModal, setShowInspectionModal] = useState(false);
    
    // --- New States for Interactivity ---
    const [searchTerm, setSearchTerm] = useState('');
    const [showAddAssetModal, setShowAddAssetModal] = useState(false);
    const [showContractModal, setShowContractModal] = useState(false);
    const [selectedRentalAsset, setSelectedRentalAsset] = useState<RentalAsset | null>(null);

    // --- Handlers ---
    const handleAddRentalAsset = (newAsset: RentalAsset) => {
        setFleet([...fleet, newAsset]);
        setShowAddAssetModal(false);
    }

    const handleCreateContract = (newContract: RentalContract) => {
        setContracts([...contracts, newContract]);
        
        // If an asset was selected for dispatch, update its status
        if (newContract.assets.length > 0) {
            setFleet(fleet.map(a => 
                a.id === newContract.assets[0].id 
                ? { ...a, status: 'On Rent', currentLocation: newContract.customer, linkedContractId: newContract.id } 
                : a
            ));
        }
        setShowContractModal(false);
        setSelectedRentalAsset(null);
    }

    const handleDispatch = (asset: RentalAsset) => {
        setSelectedRentalAsset(asset);
        setShowContractModal(true);
    }

    const filteredFleet = fleet.filter(asset => 
        asset.fleetNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        asset.make.toLowerCase().includes(searchTerm.toLowerCase()) ||
        asset.model.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // --- Dashboard Tab ---
    const renderDashboard = () => (
        <div className="space-y-6 animate-fade-in">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-slate-800 p-4 rounded border-t-4 border-titan-500">
                    <div className="text-slate-400 text-xs uppercase font-bold">Fleet Utilization</div>
                    <div className="text-3xl font-bold text-white">65%</div>
                    <div className="text-green-500 text-xs">On Rent: {fleet.filter(a => a.status === 'On Rent').length} / {fleet.length}</div>
                </div>
                <div className="bg-slate-800 p-4 rounded border-t-4 border-blue-500">
                    <div className="text-slate-400 text-xs uppercase font-bold">Projected Revenue</div>
                    <div className="text-3xl font-bold text-white">R 450k</div>
                    <div className="text-slate-500 text-xs">This Month</div>
                </div>
                <div className="bg-slate-800 p-4 rounded border-t-4 border-red-500">
                    <div className="text-slate-400 text-xs uppercase font-bold">Maintenance Due</div>
                    <div className="text-3xl font-bold text-white">2</div>
                    <div className="text-red-400 text-xs">Units Offline</div>
                </div>
                <div className="bg-slate-800 p-4 rounded border-t-4 border-green-500">
                    <div className="text-slate-400 text-xs uppercase font-bold">Active Contracts</div>
                    <div className="text-3xl font-bold text-white">{contracts.length}</div>
                    <div className="text-slate-500 text-xs">Clients Served</div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-slate-800 p-6 rounded-lg border border-slate-700">
                    <h3 className="text-lg font-bold text-white mb-4">Availability by Type</h3>
                    <div className="h-64">
                       <ResponsiveContainer width="100%" height="100%">
                           <BarChart data={[
                               { name: 'Diesel Portable', available: 1, rented: 1 },
                               { name: 'Electric Screw', available: 0, rented: 0, maintenance: 1 },
                               { name: 'Dryers', available: 5, rented: 2 }
                           ]}>
                               <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                               <XAxis dataKey="name" stroke="#94a3b8" />
                               <YAxis stroke="#94a3b8" />
                               <Tooltip contentStyle={{backgroundColor: '#1e293b', borderColor: '#334155'}} />
                               <Bar dataKey="available" fill="#10b981" name="Available" stackId="a" />
                               <Bar dataKey="rented" fill="#3b82f6" name="On Rent" stackId="a" />
                               <Bar dataKey="maintenance" fill="#ef4444" name="Maintenance" stackId="a" />
                           </BarChart>
                       </ResponsiveContainer>
                    </div>
                </div>
                
                <div className="bg-slate-800 p-6 rounded-lg border border-slate-700">
                    <h3 className="text-lg font-bold text-white mb-4">Contract Expiries</h3>
                    <div className="space-y-4">
                        {contracts.map(c => (
                            <div key={c.id} className="flex justify-between items-center bg-slate-900 p-3 rounded border border-slate-700">
                                <div>
                                    <div className="text-white font-medium">{c.customer}</div>
                                    <div className="text-xs text-slate-500">{c.site} • {c.id}</div>
                                </div>
                                <div className="text-right">
                                    <div className="text-sm font-bold text-white">{c.endDate}</div>
                                    <div className="text-xs text-orange-400">Ends in 5 days</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );

    // --- Fleet Tab ---
    const renderFleet = () => (
        <div className="space-y-6 animate-fade-in">
            <div className="flex justify-between items-center">
                <div className="flex gap-4">
                    <div className="relative">
                        <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-500" />
                        <input 
                            className="bg-slate-800 border border-slate-700 rounded-full pl-10 pr-4 py-2 text-sm text-white focus:border-titan-500 outline-none w-64" 
                            placeholder="Search fleet..." 
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <button className="bg-slate-800 border border-slate-700 text-slate-300 px-4 py-2 rounded flex items-center gap-2 hover:text-white">
                        <Filter className="w-4 h-4" /> Filter
                    </button>
                </div>
                <button onClick={() => setShowAddAssetModal(true)} className="bg-titan-500 hover:bg-titan-600 text-white px-4 py-2 rounded font-bold flex items-center gap-2">
                    <Plus className="w-4 h-4" /> Add Asset
                </button>
            </div>

            <div className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden">
                <table className="w-full text-left">
                    <thead className="bg-slate-900 text-slate-400 text-xs uppercase">
                        <tr>
                            <th className="px-6 py-4">Fleet No.</th>
                            <th className="px-6 py-4">Asset Detail</th>
                            <th className="px-6 py-4">Depot</th>
                            <th className="px-6 py-4">Status</th>
                            <th className="px-6 py-4">Daily Rate</th>
                            <th className="px-6 py-4">Current Location</th>
                            <th className="px-6 py-4">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-700">
                        {filteredFleet.map(asset => (
                            <tr key={asset.id} className="hover:bg-slate-750">
                                <td className="px-6 py-4 font-mono text-white font-bold">{asset.fleetNumber}</td>
                                <td className="px-6 py-4">
                                    <div className="text-white font-medium">{asset.make} {asset.model}</div>
                                    <div className="text-xs text-slate-500">{asset.specs}</div>
                                </td>
                                <td className="px-6 py-4 text-slate-400">{asset.depot}</td>
                                <td className="px-6 py-4"><StatusBadge status={asset.status} /></td>
                                <td className="px-6 py-4 text-slate-300">R {asset.dailyRate}</td>
                                <td className="px-6 py-4 text-sm text-slate-400">{asset.currentLocation || '-'}</td>
                                <td className="px-6 py-4">
                                    {asset.status === 'Available' ? (
                                        <button onClick={() => handleDispatch(asset)} className="text-xs bg-green-600 hover:bg-green-500 text-white px-3 py-1 rounded font-bold">Dispatch</button>
                                    ) : (
                                        <button onClick={() => setShowInspectionModal(true)} className="text-xs bg-blue-600 hover:bg-blue-500 text-white px-3 py-1 rounded font-bold">Return</button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );

    return (
        <div>
            <div className="flex justify-between items-center mb-6 min-h-[4rem]">
                <div className="flex justify-between items-center border-b border-slate-700 pb-1 w-full">
                    <div className="flex gap-6">
                        {['Dashboard', 'Fleet', 'Contracts', 'Inspections'].map(t => (
                            <button 
                                key={t}
                                onClick={() => setTab(t as any)}
                                className={`pb-3 border-b-2 transition font-medium ${tab === t ? 'border-titan-500 text-titan-500' : 'border-transparent text-slate-400 hover:text-white'}`}
                            >
                                {t}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
            {tab === 'Dashboard' && renderDashboard()}
            {tab === 'Fleet' && renderFleet()}
            
            {tab === 'Contracts' && (
                <div className="bg-slate-800 rounded-lg border border-slate-700 p-6 animate-fade-in">
                    <div className="flex justify-between mb-4">
                        <h3 className="text-lg font-bold text-white">Active Rental Contracts</h3>
                        <button onClick={() => setShowContractModal(true)} className="bg-titan-500 hover:bg-titan-600 text-white px-4 py-2 rounded flex items-center gap-2">
                            <Plus className="w-4 h-4" /> New Contract
                        </button>
                    </div>
                    <div className="space-y-4">
                        {contracts.map(c => (
                            <div key={c.id} className="bg-slate-900 p-4 rounded border border-slate-700 flex justify-between items-center hover:border-titan-500 transition">
                                <div>
                                    <div className="flex items-center gap-3">
                                        <span className="text-titan-500 font-bold">{c.id}</span>
                                        <span className="text-white font-medium">{c.customer}</span>
                                    </div>
                                    <div className="text-sm text-slate-400 mt-1">Site: {c.site} • {c.startDate} to {c.endDate}</div>
                                </div>
                                <div className="text-right flex items-center gap-6">
                                    <div className="text-sm">
                                        <div className="text-slate-500 uppercase text-xs font-bold">Assets</div>
                                        <div className="text-white">{c.assets.map(a => a.fleetNumber).join(', ')}</div>
                                    </div>
                                    <StatusBadge status={c.status} />
                                    <button className="text-slate-400 hover:text-white"><ChevronRight className="w-5 h-5"/></button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {tab === 'Inspections' && (
                <div className="bg-slate-800 rounded-lg border border-slate-700 p-6 animate-fade-in text-center text-slate-500">
                    <ClipboardCheck className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <h3 className="text-white font-bold mb-2">Digital Inspections</h3>
                    <p>No pending inspections found.</p>
                    <button onClick={() => setShowInspectionModal(true)} className="mt-4 bg-slate-700 hover:bg-slate-600 text-white px-4 py-2 rounded">Start Ad-hoc Inspection</button>
                </div>
            )}

            {showInspectionModal && (
                <Modal title="Off-Hire Inspection Checklist" onClose={() => setShowInspectionModal(false)}>
                    {/* ... (Keep existing inspection modal content) */}
                    <div className="space-y-6 p-4">
                        <div className="bg-slate-900 p-4 rounded border border-slate-700">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-xs text-slate-500 uppercase">Asset</label>
                                    <div className="text-white font-bold">DF-400 (Doosan 7/41)</div>
                                </div>
                                <div>
                                    <label className="text-xs text-slate-500 uppercase">Client</label>
                                    <div className="text-white font-bold">Anglo American</div>
                                </div>
                            </div>
                        </div>

                        <div>
                            <h4 className="text-titan-500 font-bold text-sm uppercase mb-3 border-b border-slate-700 pb-1">Checklist</h4>
                            <div className="space-y-3">
                                {['Fuel Level Checked (Full)', 'Bodywork Condition', 'Engine Oil Level', 'Tyre Condition', 'Safety Decals Present', 'Tow Hitch Secure'].map((check, i) => (
                                    <label key={i} className="flex items-center gap-3 p-2 hover:bg-slate-700/50 rounded cursor-pointer">
                                        <input type="checkbox" className="w-5 h-5 rounded bg-slate-900 border-slate-600 text-titan-500" />
                                        <span className="text-slate-300">{check}</span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <Input label="Hour Meter Reading" type="number" placeholder="e.g. 4500" />
                            <Input label="Fuel Level (%)" type="number" placeholder="100" />
                        </div>

                        <div className="flex justify-end pt-4 border-t border-slate-700 gap-3">
                            <button onClick={() => setShowInspectionModal(false)} className="px-4 py-2 text-slate-400 hover:text-white">Cancel</button>
                            <button className="bg-green-600 hover:bg-green-500 text-white px-6 py-2 rounded font-bold">Complete & Off-Hire</button>
                        </div>
                    </div>
                </Modal>
            )}

            {showAddAssetModal && (
                <AddRentalAssetModal 
                    onClose={() => setShowAddAssetModal(false)}
                    onSave={handleAddRentalAsset}
                />
            )}

            {showContractModal && (
                <CreateContractModal 
                    onClose={() => { setShowContractModal(false); setSelectedRentalAsset(null); }}
                    onSave={handleCreateContract}
                    preselectedAsset={selectedRentalAsset}
                />
            )}
        </div>
    );
}
